import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getAgentDashboard, getAgentCustomers, agentOnboard, getAgentNotifications, markAgentNotificationsRead } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, CreditCard, FileText, CheckCircle, Clock, XCircle, LogOut, Copy, UserPlus, Loader2, DollarSign, Share2, Bell, User, MapPin, Phone, ExternalLink, Banknote } from "lucide-react";
import AgentCommissions from "./AgentCommissions";

export default function AgentDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [onboardMobile, setOnboardMobile] = useState("");
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [customerFilter, setCustomerFilter] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [dashRes, custRes] = await Promise.all([getAgentDashboard(), getAgentCustomers()]);
      setStats(dashRes.data.stats);
      setAgentProfile(dashRes.data.agent);
      setReferralCode(dashRes.data.referral_code || user?.referral_code || '');
      setCustomers(custRes.data.customers || []);
      setNotifications(dashRes.data.notifications || []);
      setUnreadCount((dashRes.data.notifications || []).filter(n => !n.is_read).length);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  };

  const handleOnboard = async () => {
    if (!onboardMobile || onboardMobile.length !== 10) { toast.error("Enter valid 10-digit mobile"); return; }
    setOnboarding(true);
    try {
      const res = await agentOnboard(onboardMobile);
      toast.success(res.data.message);
      setOnboardMobile("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to onboard");
    } finally { setOnboarding(false); }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success("Referral code copied!");
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/customer/login?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const shareReferralLink = () => {
    const link = `${window.location.origin}/customer/login?ref=${referralCode}`;
    if (navigator.share) {
      navigator.share({ title: 'Join Rebuilt', text: `Use my referral code ${referralCode} to get EMI assistance`, url: link });
    } else {
      copyReferralLink();
    }
  };

  const markNotificationsRead = async () => {
    try {
      await markAgentNotificationsRead();
      setUnreadCount(0);
    } catch (err) { console.error(err); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const filteredCustomers = customers.filter(c => {
    if (customerFilter === 'all') return true;
    if (customerFilter === 'kyc_pending') return c.kyc_status !== 'completed';
    if (customerFilter === 'subscribed') return c.subscription_status === 'success';
    if (customerFilter === 'applied') return c.application_status !== 'none';
    if (customerFilter === 'disbursed') return c.disbursement_status === 'transferred';
    return true;
  });

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: Users },
    { id: "customers", label: "My Customers", icon: FileText },
    { id: "onboard", label: "Onboard", icon: UserPlus },
    { id: "commissions", label: "Commissions", icon: DollarSign },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1565C0]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-4 sm:px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {agentProfile?.profile_photo_url ? (
              <img src={agentProfile.profile_photo_url} alt="" className="w-9 h-9 rounded-xl object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-sm font-bold text-[#0A2540]">{agentProfile?.name || user?.name || 'Agent'}</h1>
              <p className="text-[10px] text-gray-500">{agentProfile?.city || ''} {agentProfile?.status === 'active' ? '• Active' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={markNotificationsRead} className="relative p-2 text-gray-500 hover:text-gray-800">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>}
            </button>
            <button onClick={handleLogout} className="p-2 text-gray-500 hover:text-red-500">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${tab === t.id ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
        {/* Dashboard Tab */}
        {tab === "dashboard" && (
          <div className="space-y-6">
            {/* Referral Code Card */}
            <div className="bg-gradient-to-br from-[#1565C0] to-[#0D47A1] rounded-2xl p-5 sm:p-6 text-white">
              <p className="text-xs font-bold uppercase tracking-wider text-white/70 mb-2">Your Referral Code</p>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl sm:text-4xl font-black tracking-wider">{referralCode || 'Pending'}</span>
                {referralCode && (
                  <button onClick={copyReferralCode} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                    <Copy className="h-4 w-4" />
                  </button>
                )}
              </div>
              {referralCode && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={copyReferralLink} className="flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 rounded-full px-4 py-2 text-sm font-semibold transition-colors">
                    <Copy className="h-3.5 w-3.5" /> Copy Referral Link
                  </button>
                  <button onClick={shareReferralLink} className="flex items-center justify-center gap-2 bg-white text-[#1565C0] hover:bg-white/90 rounded-full px-4 py-2 text-sm font-semibold transition-colors">
                    <Share2 className="h-3.5 w-3.5" /> Share
                  </button>
                </div>
              )}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Total Referred" value={stats?.total_referred || 0} color="blue" />
              <StatCard icon={CreditCard} label="Paid Subscription" value={stats?.paid_subscription || 0} color="green" />
              <StatCard icon={Clock} label="Apps Pending" value={stats?.applications?.pending || 0} color="amber" />
              <StatCard icon={CheckCircle} label="Apps Approved" value={stats?.applications?.approved || 0} color="teal" />
            </div>

            {/* Notifications */}
            {notifications.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                  <Bell className="h-4 w-4" /> Recent Notifications
                </h3>
                <div className="space-y-2">
                  {notifications.slice(0, 3).map(n => (
                    <div key={n.id} className={`p-3 rounded-xl text-sm ${n.is_read ? 'bg-gray-50' : 'bg-blue-50 border border-blue-100'}`}>
                      <p className="font-semibold text-[#0A2540]">{n.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Customers Tab */}
        {tab === "customers" && (
          <div className="space-y-4">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                { id: 'all', label: 'All' },
                { id: 'kyc_pending', label: 'KYC Pending' },
                { id: 'subscribed', label: 'Subscribed' },
                { id: 'applied', label: 'Applied' },
                { id: 'disbursed', label: 'Disbursed' },
              ].map(f => (
                <button key={f.id} onClick={() => setCustomerFilter(f.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${customerFilter === f.id ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>
                  {f.label}
                </button>
              ))}
            </div>

            {filteredCustomers.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No customers found</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Customer</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">KYC</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Subscription</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Application</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Disbursement</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredCustomers.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold">{c.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-400">{c.mobile ? c.mobile.slice(0, 2) + '****' + c.mobile.slice(-4) : ''}</div>
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={c.kyc_status === 'completed' ? 'completed' : 'pending'} /></td>
                          <td className="px-4 py-3"><StatusBadge status={c.subscription_status === 'success' ? 'paid' : c.subscription_status || 'none'} /></td>
                          <td className="px-4 py-3"><StatusBadge status={c.application_status || 'none'} /></td>
                          <td className="px-4 py-3"><StatusBadge status={c.disbursement_status === 'transferred' ? 'disbursed' : c.disbursement_status || 'none'} /></td>
                          <td className="px-4 py-3 text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Onboard Tab */}
        {tab === "onboard" && (
          <div className="space-y-6">
            {/* Method A: Direct */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F0F4FF] flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-[#1565C0]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A2540]">Direct Customer Onboarding</h3>
                  <p className="text-xs text-gray-500">Enter customer's mobile to link them to your account</p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex gap-2 flex-1">
                  <span className="bg-gray-100 rounded-xl px-3 flex items-center text-sm text-gray-600 font-semibold">+91</span>
                  <Input value={onboardMobile} onChange={e => setOnboardMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Customer mobile" maxLength={10} className="rounded-xl" />
                </div>
                <Button onClick={handleOnboard} disabled={onboarding} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-xl">
                  {onboarding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link"}
                </Button>
              </div>
            </div>

            {/* Method B: Share Link */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Share2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A2540]">Share Referral Link</h3>
                  <p className="text-xs text-gray-500">Share your unique link — customers are linked automatically</p>
                </div>
              </div>
              {referralCode ? (
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-sm font-mono text-gray-600 break-all">
                    {window.location.origin}/customer/login?ref={referralCode}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={copyReferralLink} variant="outline" className="flex-1 rounded-xl">
                      <Copy className="h-4 w-4 mr-2" /> Copy Link
                    </Button>
                    <Button onClick={shareReferralLink} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl">
                      <Share2 className="h-4 w-4 mr-2" /> Share
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Referral code pending. Contact admin.</p>
              )}
            </div>
          </div>
        )}

        {/* Commissions Tab */}
        {tab === "commissions" && <AgentCommissions />}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    teal: "bg-teal-50 text-teal-600 border-teal-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color] || colorMap.blue}`}>
      <div className="flex items-center gap-1 mb-1"><Icon className="h-4 w-4" /></div>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-[10px] uppercase tracking-wider font-bold mt-0.5">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    completed: "bg-green-100 text-green-800",
    paid: "bg-green-100 text-green-800",
    success: "bg-green-100 text-green-800",
    approved: "bg-green-100 text-green-800",
    disbursed: "bg-blue-100 text-blue-800",
    transferred: "bg-blue-100 text-blue-800",
    pending: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-800",
    none: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[status] || styles.none}`}>
      {status === 'none' ? '—' : (status || '—').charAt(0).toUpperCase() + (status || '').slice(1)}
    </span>
  );
}
